import classnames from 'classnames';
import Image from 'next/image';
import PropTypes from 'prop-types';
import Text from './Text';

const buttonStyles = {
    primary: {
        default: 'bg-primary-300 border border-transparent text-gray-50 active:bg-primary-300 visited:bg-primary-300',
        gray: 'bg-gray-100 border border-transparent text-gray-50 focus:bg-gray-100 active:bg-gray-100 visited:bg-gray-100',
        dull: '!text-gray-200 bg-gray-800',
        success: 'bg-success-500 border border-transparent text-gray-50 active:bg-success-500 visited:bg-success-500',
        danger: 'bg-error-300 border border-transparent text-gray-50 focus:bg-error-300 active:bg-error-300 visited:bg-error-300',
        idb: 'bg-idb-500 border border-transparent !text-basic-200 active:bg-idb-500 visited:bg-idb-500'
    },
    ghost: {
        default: 'text-primary-300 border border-primary-300',
        gray: 'text-gray-100 border border-gray-400 focus:outline-2 visited:text-gray-300',
        danger: 'text-gray-100 border border-error-300 bg-error-350 focus:outline-2 visited:text-gray-300',
        success: 'text-gray-100 border border-success-200 bg-success-350 focus:outline-2 visited:text-gray-300',
        neutral: 'text-gray-100 border border-others-100 bg-[#DCBC0F]/25 focus:outline-2 visited:text-gray-300',
        pink: 'text-gray-100 border border-others-150 bg-[#F79DBB]/25 focus:outline-2 visited:text-gray-300',
        idb: 'text-gray-100 border border-idb-500 bg-idb-500/25 focus:outline-2 visited:text-gray-300',
        dark: 'text-gray-100 border border-gray-600 bg-basic-200',
        muted: 'text-gray-200 border border-gray-600',
        selected: 'text-success-500 border border-success-500',
        ticket: 'text-gray-100 border border-gray-500',
        ticketDanger: 'text-error-300 border border-error-300'
    },
    outline: {
        default: 'text-primary-300 border border-primary-300 bg-transparent',
        success: 'text-success-500 border border-success-500 bg-transparent',
        idb: 'text-idb-500 border border-idb-500 bg-transparent'
    },
    borderLess: {
        default: 'text-primary-300 border border-transparent bg-transparent disabled:!bg-transparent',
        gray: 'text-gray-100 border border-transparent bg-transparent',
        success: 'text-success-500'
    }
};

const disabledStyles = {
    default: 'bg-primary-110 text-primary-100 border-transparent',
    gray: 'bg-gray-700 text-gray-300 border-transparent',
    dull: 'bg-gray-800 text-gray-400 border-transparent',
    success: 'bg-success-500/50 text-gray-100 border-transparent',
    danger: 'bg-error-300/50 text-error-200 border-transparent',
    idb: 'bg-idb-500/30 text-basic-200 border-transparent',
    neutral: 'bg-[#DCBC0F]/10 text-[#DCBC0F]/50 border-transparent',
    pink: 'bg-[#F79DBB]/10 text-[#F79DBB]/50 border-transparent',
    ticket: 'text-gray-300 border border-gray-600',
    ticketDanger: 'text-error-300/50 border border-gray-600'
};

const iconPositionsStyle = {
    left: 'flex-row',
    right: 'flex-row-reverse space-x-reverse'
};

const Button = ({
    iconPosition,
    label,
    onClick,
    type,
    fullWidth,
    className,
    size,
    disabled,
    btnClass,
    iconComponent,
    buttonTheme,
    title,
    loading,
    btnRef,
    onKeyDown,
    textSize,
    iconClass,
    loadingText
}) => {
    const buttonSizes = {
        compact: `border py-[7px] px-6 font-semibold leading-5 whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-300 ${textSize || 'text-sm'}`,
        ticket: `border py-[7px] px-4 font-semibold leading-4 whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-300 ${textSize || 'text-xs'}`,
        tiny: `py-[3px] px-2 font-semibold leading-5 ${textSize || 'text-xs'}`,
        chip: `!rounded py-[5px] px-2 font-normal leading-4 whitespace-nowrap ${textSize || 'text-xs'}`,
        small: `py-[11px] px-6 font-semibold ${textSize || 'text-sm'}`,
        extraSmall: `py-[5px] px-6 font-semibold ${textSize || 'text-base'}`,
        medium: `py-[9px] px-8 font-semibold ${textSize || 'text-base'}`,
        large: `py-[15px] px-8 font-semibold ${textSize || 'text-xl'}`
    };

    const defaultStyle = buttonStyles[type]?.default || buttonStyles.primary.default;
    const themeStyle = buttonStyles[type]?.[buttonTheme] || defaultStyle;

    const disabledStyle = disabledStyles[buttonTheme]
        || disabledStyles.default;

    const buttonClasses = classnames(
        'transition-all rounded-md disabled:cursor-not-allowed disabled:outline-none disabled:transform-none transform transition-transform duration-300 flex items-center space-x-2 disabled:opacity-50',
        disabled ? disabledStyle : themeStyle,
        fullWidth && 'w-full justify-center',
        className,
        btnClass,
        label && buttonSizes[size],
        iconPositionsStyle[iconPosition],
        !loading && !disabled && 'hover:opacity-80 active:scale-90'
    );

    return (
        <button
            {...(btnRef && { ref: btnRef })}
            {...(onKeyDown && { onKeyDown })}
            title={title}
            type="button"
            onClick={onClick}
            className={`flex items-center gap-1 ${buttonClasses}`}
            disabled={disabled}
        >
            <div
                className={`flex ${iconComponent && 'space-x-2'}  w-full relative ${iconClass} ${iconPositionsStyle[iconPosition]}`}
            >
                <div className={`${loading && 'invisible'} flex`}>
                    {iconComponent && iconComponent}
                </div>

                {label && (
                    <span className={`${loading && 'invisible'} w-full`}>
                        {label}
                    </span>
                )}

                {loading && (
                    <div
                        className={`absolute top-1/2 left-1/2 transform translate-x-[-50%] translate-y-[-50%] ${loadingText && 'flex items-center gap-1'
                        }`}
                    >
                        <div className="min-w-5 flex items-center">
                            <Image
                                src="/images/loader.svg"
                                width="22"
                                height="22"
                                alt="loading"
                                className="animate-spin"
                            />
                        </div>

                        {loadingText && (
                            <Text
                                variant="bodySmall"
                                fontWeight="font-semibold"
                                className="text-gray-200"
                            >
                                {loadingText}
                            </Text>
                        )}
                    </div>
                )}
            </div>
        </button>
    );
};

Button.propTypes = {
    type: PropTypes.oneOf(Object.keys(buttonStyles)),
    size: PropTypes.oneOf(['small', 'extraSmall', 'medium', 'large', 'tiny', 'chip', 'ticket', 'compact']),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    disabled: PropTypes.bool,
    iconComponent: PropTypes.node,
    onClick: PropTypes.func,
    fullWidth: PropTypes.bool,
    className: PropTypes.string,
    btnClass: PropTypes.string,
    iconPosition: PropTypes.oneOf(['left', 'right']),
    buttonTheme: PropTypes.string,
    loading: PropTypes.bool,
    textSize: PropTypes.string
};

Button.defaultProps = {
    iconComponent: null,
    iconPosition: 'left',
    label: '',
    onClick: () => { },
    type: 'primary',
    fullWidth: false,
    className: '',
    disabled: false,
    size: 'small',
    btnClass: '',
    buttonTheme: 'default',
    loading: false
};

export default Button;
